import { useState, useMemo } from 'react';
import { Search, Download } from 'lucide-react';
import { Input } from '../ui/input';
import { Select } from '../ui/select-custom';
import { Button } from '../ui/button';
import { ExpenseItem } from './expense-item';
import { CsvPreviewDialog } from '../dialogs/csv-preview-dialog';
import { categoryTypes } from '../../types';
import { formatCurrency } from '../../lib/format';
import type { Expense, CategoryType, Tag } from '../../types';
import type { ComponentProps } from 'react';

const allCategories = (['needs', 'comfort'] as CategoryType[]);

const sortOptions = [
  { value: 'date', label: 'Data' },
  { value: 'value', label: 'Maior valor' },
];

export interface ExpenseListProps extends Omit<
  ComponentProps<'div'>,
  'children'
> {
  expenses: Expense[];
  onEdit?: (expense: Expense) => void;
  onDelete?: (expense: Expense) => void;
  onRepeat?: (expense: Expense) => void;
  availableTags?: Tag[];
}

export function ExpenseList({
  expenses,
  onEdit,
  onDelete,
  onRepeat,
  availableTags = [],
  ...props
}: ExpenseListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'date' | 'value'>('date');
  const [selectedCategory, setSelectedCategory] = useState<CategoryType | 'all'>('all');
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [csvPreviewOpen, setCsvPreviewOpen] = useState(false);

  const toggleTag = (tagId: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  };

  const filteredExpenses = useMemo(() => {
    let filtered = expenses;

    // Filter by search query
    if (searchQuery) {
      filtered = filtered.filter(
        (expense) =>
          expense.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          expense.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((expense) =>
        expense.categoryType === selectedCategory
      );
    }

    // Filter by tags (must contain ALL selected tags)
    if (selectedTagIds.length > 0) {
      filtered = filtered.filter((expense) =>
        selectedTagIds.every((tagId) => expense.tags?.includes(tagId))
      );
    }

    // Sort
    filtered = [...filtered].sort((a, b) => {
      let fa;
      let fb;
      if (sortBy === 'date') {
        fa = new Date(a.date).getTime();
        fb = new Date(b.date).getTime();
      } else {
        fa = b.value;
        fb = a.value;
      }

      if (fa === fb) {
        fa = new Date(a.createdAt).getTime();
        fb = new Date(b.createdAt).getTime();
      }

      return fa - fb;
    });

    return filtered;
  }, [expenses, searchQuery, sortBy, selectedCategory, selectedTagIds]);

  const totalValue = filteredExpenses.reduce(
    (sum, expense) => sum + expense.value,
    0
  );

  return (
    <div data-slot="expense-list" className="flex flex-col gap-4" {...props}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          type="text"
          placeholder="Buscar gastos..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* Category filter chips */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setSelectedCategory('all')}
          className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
            selectedCategory === 'all'
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border bg-surface text-foreground-subtle hover:bg-muted'
          }`}
        >
          Todas
        </button>
        {allCategories.map((category) => {
          const info = categoryTypes[category];
          const isActive = selectedCategory === category;
          return (
            <button
              key={category}
              type="button"
              onClick={() =>
                setSelectedCategory(isActive ? 'all' : category)
              }
              className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                isActive
                  ? 'border-transparent text-white'
                  : 'border-border bg-surface text-foreground-subtle hover:bg-muted'
              }`}
              style={isActive ? { backgroundColor: info.color } : undefined}
            >
              {!isActive && (
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: info.color }}
                />
              )}
              <span>{info.label}</span>
            </button>
          );
        })}
      </div>

      {availableTags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {availableTags.map((tag) => {
            const isActive = selectedTagIds.includes(tag.id);
            return (
              <button
                key={tag.id}
                type="button"
                onClick={() => toggleTag(tag.id)}
                className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors cursor-pointer ${
                  isActive
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-surface text-foreground-subtle hover:bg-muted'
                }`}
              >
                <span>{tag.icon}</span>
                <span>{tag.name}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-muted p-3">
        <div className="flex items-center gap-3">
          <Select
            value={sortBy}
            onChange={(value) => setSortBy(value as 'date' | 'value')}
            options={sortOptions}
          />
          <span className="text-sm text-foreground-subtle">
            {filteredExpenses.length}{' '}
            {filteredExpenses.length === 1 ? 'gasto' : 'gastos'}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-foreground-subtle">Total:</span>
          <span className="text-lg font-bold text-foreground">
            R$ {formatCurrency(totalValue)}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={filteredExpenses.length === 0}
            onClick={() => setCsvPreviewOpen(true)}
          >
            <Download />
            Baixar CSV
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {filteredExpenses.map((expense) => (
          <ExpenseItem
            key={expense.id}
            expense={expense}
            onEdit={onEdit}
            onDelete={onDelete}
            onRepeat={onRepeat}
            availableTags={availableTags}
          />
        ))}

        {filteredExpenses.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-surface py-12 text-center">
            <span className="text-sm text-muted-foreground">
              {searchQuery || selectedCategory !== 'all'
                ? 'Nenhum gasto encontrado com os filtros aplicados'
                : 'Nenhum gasto registrado ainda'}
            </span>
          </div>
        )}
      </div>

      <CsvPreviewDialog
        open={csvPreviewOpen}
        onClose={() => setCsvPreviewOpen(false)}
        expenses={filteredExpenses}
        availableTags={availableTags}
        filename="gastos.csv"
      />
    </div>
  );
}
